import { Injectable, inject } from '@angular/core';
import { InsertionPlanChoiceService } from 'src/app/collections/inspection-plan-assistant/insertion-plan-choice.service';
import { HttpClient } from '@angular/common/http';
import { AutomaticPoints } from 'src/app/automatic-points/_models/AutomaticPoints';
import { GeneralSettings } from 'src/app/automatic-points/_models/GeneralSettings';
import { PlatesSettings } from 'src/app/automatic-points/_models/PlatesSettings';
import { StiffenersSettings } from 'src/app/automatic-points/_models/StiffenersSettings';
import { FacePlatesSettings } from 'src/app/automatic-points/_models/FacePlatesSettings';
import { BracketSettings } from 'src/app/automatic-points/_models/BracketSettings';
import { PlatesPointCriteria } from 'src/app/automatic-points/_models/PlatesPointCriteria';
import { environment } from 'src/environments/environment';
import { StorageService, User, SESSION_STORAGE, Util } from '@phdsoft/common';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { v4 as uuid } from 'uuid';
import { ClusterService } from 'src/app/cluster/cluster.service';
import { ClusterState } from 'src/app/cluster/cluster.state';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { ClusterElementViewModel } from 'projects/phdsoft/common/src/lib/models/cluster-element.model';
import { INSPECTIONS_TYPES_IDS } from 'projects/phdsoft/common/src/lib/shared/constants/inspections-types-ids';
import moment from 'moment';
import { PointsArrangements } from '../_models/PointsArrangements';

@Injectable({
  providedIn: 'root'
})
export class CollectionInspectionGauginsWizardService {
  /** Where the day is in several open plans, which one the new points go to. */
  private readonly insertionPlanChoice = inject(InsertionPlanChoiceService);

  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly assetManager: AssetManager,
    private readonly clusterService: ClusterService,
    private readonly clusterState: ClusterState
  ) { }

  /**
   * Execute the automatic points generation with default configuration
   * @param inspectionTypeId The inspection type ID
   * @param inspectionMethodId The inspection method ID
   * @param inspectionTypeName The name of the inspection type
   * @param checkLists Optional checklists
   * @returns Promise with the response data
   */
  async executeWithDefaultConfig(
    inspectionTypeId: string,
    inspectionMethodId: string,
    inspectionTypeName: string,
    checkLists?: any[]
  ) {
    const autoPoints = this.createDefaultConfiguration(
      inspectionTypeId,
      inspectionMethodId,
      inspectionTypeName,
      checkLists
    );

    return this.executeAutomaticPoints(autoPoints);
  }

  /**
   * Creates a default configuration for automatic points
   */
  private createDefaultConfiguration(
    inspectionTypeId: string,
    inspectionMethodId: string,
    inspectionTypeName: string,
    checkLists?: any[]
  ): AutomaticPoints {
    const assetId = this.assetManager.asset.assetInfo.AssetId;
    const automaticPoints = new AutomaticPoints();

    // Basic configuration
    automaticPoints.Id = null;
    automaticPoints.AssetId = assetId;
    automaticPoints.CollectionId = null;
    automaticPoints.Label = "AutomaticPoints";
    automaticPoints.InspectionTypeId = inspectionTypeId;
    automaticPoints.InspectionMethodId = inspectionMethodId;
    automaticPoints.InspectionTypeName = inspectionTypeName;
    automaticPoints.InspectionTypeAlias = this.assetManager.listInpectionType?.find(t => t.id === inspectionTypeId)?.alias;
    automaticPoints.CheckLists = checkLists || [];
    automaticPoints.Date = this.assetManager.referenceDate;

    // Add general settings
    automaticPoints.GeneralSettings = new GeneralSettings();
    automaticPoints.GeneralSettings.CurrentPage = true;
    automaticPoints.GeneralSettings.AllPages = false;
    automaticPoints.GeneralSettings.BulkheadsNoStifffners = true;
    automaticPoints.GeneralSettings.DeleteOldAutomaticPoints = false;
    automaticPoints.GeneralSettings.Date = moment(this.assetManager.referenceDate).format("yyyy-MM-DD");

    // Get visible regions and sections
    const regions = this.getVisibleRegions();
    automaticPoints.GeneralSettings.Regions = regions;

    // Configure plates
    automaticPoints.PlatesSettings = new PlatesSettings();
    automaticPoints.PlatesSettings.PositionGaugingsforPlates = true;
    automaticPoints.PlatesSettings.PositionGaugingsforBays = false;
    automaticPoints.PlatesSettings.PositionGaugingsforCriticalAreas = false;
    automaticPoints.PlatesSettings.CriticalAreaThreshold = 0;
    automaticPoints.PlatesSettings.PositionGaugingsforAnomalies = false;
    automaticPoints.PlatesSettings.AnomaliesThreshold = 0;
    automaticPoints.PlatesSettings.PointsArrangements = PointsArrangements.OnVertices;

    const platesPointCriteria: PlatesPointCriteria[] = [
      { Area: 0.09, NumberOfPoints: 1, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 0.50, NumberOfPoints: 2, AddCenterPoint: true, NumberOfRows: 0 },
      { Area: 1.00, NumberOfPoints: 4, AddCenterPoint: true, NumberOfRows: 0 }
    ];
    automaticPoints.PlatesSettings.PositionPointCriteriaList = platesPointCriteria;
    automaticPoints.PlatesSettings.MinimumDistanceBetweenPoints = 0.5;
    automaticPoints.PlatesSettings.MinimumDistanceFromEdges = 0.12;
    automaticPoints.PlatesSettings.MinimumDistanceFromElements = 0.05;
    // Configure stiffeners
    automaticPoints.StiffenersSettings = new StiffenersSettings();
    automaticPoints.StiffenersSettings.PositionGaugingPointsOnStiffeners = true;
    automaticPoints.StiffenersSettings.PositionGaugingPointsOnBays = false;
    automaticPoints.StiffenersSettings.InsertPointsOnWeb = true;
    automaticPoints.StiffenersSettings.InsertPointsOnFlange = true;
    automaticPoints.StiffenersSettings.PositionGaugingsforCriticalAreas = false;
    automaticPoints.StiffenersSettings.CriticalAreaThreshold = 0;
    automaticPoints.StiffenersSettings.PositionPointCriteriaList = [
      { Length: 0.9, NumberOfPoints: 1 },
      { Length: 4, NumberOfPoints: 2 },
      { Length: 12, NumberOfPoints: 3 }
    ];

    // Configure face plates
    automaticPoints.FacePlatesSettings = new FacePlatesSettings();
    automaticPoints.FacePlatesSettings.PositionGaugingPointsOnFacePlates = true;
    automaticPoints.FacePlatesSettings.PositionGaugingPointsOnBays = false;
    automaticPoints.FacePlatesSettings.PositionGaugingsforCriticalAreas = false;
    automaticPoints.FacePlatesSettings.CriticalAreaThreshold = 0;
    automaticPoints.FacePlatesSettings.PositionPointCriteriaList = [
      { Length: 0.01, NumberOfPoints: 1 },
      { Length: 5, NumberOfPoints: 3 }
    ];

    // Configure brackets
    automaticPoints.BracketSettings = new BracketSettings();
    automaticPoints.BracketSettings.PositionGaugingPointsOnBrackets = true;
    automaticPoints.BracketSettings.PositionGaugingPointsOnBays = false;
    automaticPoints.BracketSettings.PositionGaugingsforCriticalAreas = false;
    automaticPoints.BracketSettings.CriticalAreaThreshold = 0;
    automaticPoints.BracketSettings.PointsArrangement = PointsArrangements.Pattern;
    automaticPoints.BracketSettings.PositionPointCriteriaList = [
      { Area: 0.5, NumberOfPointsWeb: 1, NumberOfPointsFlange: 1 },
      { Area: 5, NumberOfPointsWeb: 3, NumberOfPointsFlange: 3 }
    ];

    return automaticPoints;
  }

  /**
   * Get visible regions and sections from the asset manager
   */
  private getVisibleRegions() {
    const regions = [];
    const visibleRegions = this.assetManager.asset.GetStructureVisibleRegions();

    for (const region of visibleRegions) {
      if (!region.Guid) continue;

      const sections = region.sections
        .filter(section => section.visible)
        .map(section => ({
          SectionId: section.Guid,
          SectionName: section.Name,
          SectionChecked: true
        }));

      const sectionIds = sections.map(section => section.SectionId);

      regions.push({
        RegionId: region.Guid,
        RegionName: region.Name,
        RegionChecked: true,
        SectionIds: sectionIds,
        Sections: sections
      });
    }

    return regions;
  }

  /**
   * Execute automatic points generation
   */
  private async executeAutomaticPoints(autoPoints: AutomaticPoints) {
    // Filter empty values
    autoPoints.PlatesSettings.PositionPointCriteriaList = this.filterEmptyValues(autoPoints.PlatesSettings.PositionPointCriteriaList);
    autoPoints.StiffenersSettings.PositionPointCriteriaList = this.filterEmptyValues(autoPoints.StiffenersSettings.PositionPointCriteriaList);
    autoPoints.FacePlatesSettings.PositionPointCriteriaList = this.filterEmptyValues(autoPoints.FacePlatesSettings.PositionPointCriteriaList);
    autoPoints.BracketSettings.PositionPointCriteriaList = this.filterEmptyValuesBrackets(autoPoints.BracketSettings.PositionPointCriteriaList);

    // Set user and collection ID
    const user = await this.storageService.getItem<any>(SESSION_STORAGE.USER) as User;
    autoPoints.UserId = user.id;
    autoPoints.CollectionId = uuid().replace(/-/g, '') as string;
    if (!(await this.insertionPlanChoice.prepareBotRequest(autoPoints))) return null;

    // Make the API request
    const response = await this.http.post<any>(
      environment.API_INSPEC + 'UtmPoints/signalrutmautomaticinspectionpoints',
      autoPoints
    ).toPromise();

    return response;
  }

  /**
   * Filter empty values from criteria lists
   */
  private filterEmptyValues(values: any[]) {
    return values.filter(item => !Util.isNullOrUndefined(item.NumberOfPoints));
  }

  /**
   * Filter empty values from bracket criteria lists
   */
  private filterEmptyValuesBrackets(values: any[]) {
    return values.filter(item => !Util.isNullOrUndefined(item.NumberOfPointsWeb));
  }
}
